import { inject, Signal } from '@angular/core';

import {
  injectQuery,
  Query,
  QueryClient,
} from '@tanstack/angular-query-experimental';

import { firstValueFrom } from 'rxjs';

import { ContentFilters } from 'src/app/core/models/core.models';
import { NomObjectClass, WidgetType } from 'src/app/core/constants';
import { augmentWidgetFields } from '../normalize/widget.normalize';
import {
  duplicateChartTypesWithVisualization,
  WidgetPreset,
} from 'src/app/core/models/widget.models';
import { FeedApiService } from '../services/feed-api.service';
import { NomenclatureApiService } from '../services/nomenclature-api.service';
import { WidgetApiService } from '../services/widget-api.service';

export interface WidgetPresetsScope {
  objectTypeId: number;
  objectClassId: number;
}

/** Widget presets (the gallery catalogue) for an object type/class. */
export function injectWidgetPresetsQuery(
  scope: Signal<WidgetPresetsScope | null>,
) {
  const feedApi = inject(FeedApiService);

  return injectQuery(() => ({
    queryKey: [
      'feed',
      'applicable_charts',
      {
        objectTypeId: scope()?.objectTypeId ?? null,
        objectClassId: scope()?.objectClassId ?? null,
      },
    ],
    queryFn: () =>
      firstValueFrom(
        feedApi.getApplicableCharts(
          scope().objectTypeId,
          scope().objectClassId,
        ),
      ).then((presets) => (presets ?? []).map(withDedupedChartTypes)),
    enabled: !!scope(),
  }));
}

function withDedupedChartTypes(preset: WidgetPreset): WidgetPreset {
  // A malformed preset keeps its raw chart types rather than breaking the list.
  try {
    return {
      ...preset,
      chart_types: duplicateChartTypesWithVisualization(preset.chart_types),
    };
  } catch {
    return preset;
  }
}

/**
 * Widget preset groups — the `chart_group` nomenclature, used for the gallery's
 * group filter. The "All widgets" entry is prepended at the call site.
 */
export function injectWidgetPresetGroupsQuery() {
  const nomenclatureApi = inject(NomenclatureApiService);

  return injectQuery(() => ({
    queryKey: ['nom', 'nomenclature', 'chart_group'],
    queryFn: () =>
      firstValueFrom(nomenclatureApi.getNomenclatureRecords('chart_group')),
  }));
}

/** Visualization types available for a chart. One cache entry per chartId. */
export function injectWidgetVizTypesQuery(chartId: Signal<number | null>) {
  const widgetApi = inject(WidgetApiService);

  return injectQuery(() => ({
    queryKey: ['widget', 'chart_types', chartId()],
    queryFn: () =>
      firstValueFrom(widgetApi.getVizTypes(chartId())).then((vizTypes) =>
        vizTypes ? duplicateChartTypesWithVisualization(vizTypes) : vizTypes,
      ),
    enabled: !!chartId() && chartId() > 0,
  }));
}

export interface PresentationWidgetsScope {
  objectId: string;
  objectClassId: number;
}

/**
 * The **all-pages** widget list (`page_id` absent), i.e. Presentation Mode's.
 *
 * `pageId: null` is spelled out on purpose: a dashboard *page*'s list is a
 * sibling key with a real `pageId`, and the two must never collapse onto one
 * entry — see WAP-874, where presentation copies reached the dashboard's
 * gridstack, collided, got compacted and persisted mangled positions. A feed's
 * own list is also `pageId: null`, but on a different `objectId`.
 */
export function presentationWidgetsQueryKey(scope: PresentationWidgetsScope) {
  return [
    'widget',
    'get_object_widgets',
    {
      objectId: scope.objectId,
      objectClassId: scope.objectClassId,
      pageId: null,
    },
  ];
}

/**
 * Imperative because the consumer is: `initializePresentation` awaits the list,
 * builds its slides from that snapshot, then prefetches page by page. Nothing
 * mutates this list while a presentation is open, so an observer buys nothing.
 */
export function injectFetchPresentationWidgets() {
  const widgetApi = inject(WidgetApiService);
  const queryClient = inject(QueryClient);

  return (scope: PresentationWidgetsScope) =>
    queryClient.fetchQuery({
      queryKey: presentationWidgetsQueryKey(scope),
      queryFn: () =>
        firstValueFrom(
          widgetApi.getObjectWidgets(scope.objectId, scope.objectClassId),
        ).then((widgets) =>
          (widgets ?? [])
            .filter(
              (widget) =>
                widget.widget_type_id !== WidgetType.WIDGET_TYPE_PLACEHOLDER,
            )
            .map(augmentWidgetFields),
        ),
      staleTime: 0,
      retry: false,
    });
}

export interface ObjectWidgetsScope {
  objectId: string;
  objectClassId: number;
  /** A dashboard **page**'s widgets. Always null for a feed. */
  pageId: number | null;
}

/**
 * The widget list a feed or a single dashboard page renders. Sibling of
 * `presentationWidgetsQueryKey` and never the same entry — read WAP-874 there
 * before "simplifying" the two into one key.
 */
export function objectWidgetsQueryKey(scope: ObjectWidgetsScope) {
  return [
    'widget',
    'get_object_widgets',
    {
      objectId: scope.objectId,
      objectClassId: scope.objectClassId,
      pageId: scope.pageId ?? null,
    },
  ];
}

/**
 * A dashboard without a selected page has no list to fetch. The guard also makes
 * the WAP-874 collision structurally impossible: this query can never be issued
 * with `pageId: null` on a dashboard, which is Presentation Mode's key.
 */
function isFetchableWidgetsScope(scope: ObjectWidgetsScope | null): boolean {
  if (!scope?.objectId) {
    return false;
  }

  return scope.objectClassId !== NomObjectClass.DASHBOARD || !!scope.pageId;
}

/**
 * The current view's widget list. Placeholders are **kept** (the feed renders one
 * as its "add widget" tile and `createWidget` reuses its geometry); Presentation
 * Mode's own query is the one that strips them.
 */
export function injectObjectWidgetsQuery(
  scope: Signal<ObjectWidgetsScope | null>,
) {
  const widgetApi = inject(WidgetApiService);

  return injectQuery(() => ({
    queryKey: scope()
      ? objectWidgetsQueryKey(scope())
      : ['widget', 'get_object_widgets', null],
    queryFn: () =>
      firstValueFrom(
        widgetApi.getObjectWidgets(
          scope().objectId,
          scope().objectClassId,
          scope().pageId ?? undefined,
        ),
      ).then((widgets) => (widgets ?? []).map(augmentWidgetFields)),
    enabled: isFetchableWidgetsScope(scope()),
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  }));
}

/** Everything the `/widget/:id/widget_data` request needs. */
export interface WidgetDataScope {
  widgetId: string;
  contentFilters: ContentFilters;
  languageId: number;
  cacheEnabled: boolean;
}

/**
 * The key only carries the parts of `ContentFilters` the widget request is
 * *semantically* keyed on. `offset` / `items_count` / `sort` / `order` are the
 * post list's paging and sorting state, which `getContentFilters` also returns:
 * keying on them would refetch every widget when the user pages or re-sorts the
 * post list. The queryFn still sends the full object.
 */
function widgetDataFilterKey(contentFilters: ContentFilters) {
  return {
    time_start: contentFilters?.time_start ?? null,
    time_end: contentFilters?.time_end ?? null,
    selected_only: contentFilters?.selected_only ?? false,
    filters: contentFilters?.filters ?? {},
  };
}

/**
 * Mirrors POST `/widget/:widgetId/widget_data`.
 *
 * `cacheEnabled` is deliberately **not** in the key: it is a fetch strategy ("may
 * the server serve a cached computation?"), not resource identity. That is what
 * lets Presentation Mode's prefetch force it `true` while a normal card passes
 * the dashboard's flag and both still share one cache entry, so the cards can
 * observe prefetched data.
 */
export function widgetDataQueryKey(scope: WidgetDataScope) {
  return [
    'widget',
    scope.widgetId,
    'widget_data',
    widgetDataFilterKey(scope.contentFilters),
    scope.languageId ?? null,
  ];
}

/** Every cached data query for one widget, across filter/language keys. */
export function widgetDataQueriesFilter(widgetId: string) {
  return { queryKey: ['widget', widgetId, 'widget_data'] };
}

/**
 * Every cached object-widgets list (`objectWidgetsQueryKey`), whichever object
 * or page it belongs to. Widget cards render feed and dimension titles from
 * these rows, so a feed write invalidates them (`injectFeedWriteRefresh`).
 */
export function objectWidgetsQueriesFilter() {
  return { queryKey: ['widget', 'get_object_widgets'] };
}

/**
 * Every cached widget-data query. Needs a predicate rather than a plain
 * `['widget']` prefix, which would also match `['widget','chart_types',…]`.
 */
export function allWidgetDataQueriesFilter() {
  return {
    queryKey: ['widget'],
    predicate: (query: Query) => query.queryKey[2] === 'widget_data',
  };
}

/**
 * Per-widget data, owned by `widget-card` - one query per card.
 *
 * `enabled` is the card's lazy-load latch: on for a real card once it first
 * becomes visible, off for placeholders, preview cards and Presentation Mode
 * (whose data is prefetched page by page).
 *
 * Config is deliberately conservative, because widget data is the most expensive
 * request in the app: `staleTime: 0` so an invalidation or `refetch()` always
 * hits the server, no focus/reconnect refetch (which with `staleTime: 0` would
 * reload every mounted card on tab-return), and `retry: false` so a failure does
 * not hold the spinner for seconds.
 */
export function injectWidgetDataQuery(
  scope: Signal<WidgetDataScope | null>,
  enabled: Signal<boolean>,
) {
  const widgetApi = inject(WidgetApiService);

  return injectQuery(() => ({
    queryKey: scope()
      ? widgetDataQueryKey(scope())
      : ['widget', null, 'widget_data'],
    queryFn: () =>
      firstValueFrom(
        widgetApi.getWidgetData(
          scope().widgetId,
          scope().contentFilters,
          scope().languageId,
          scope().cacheEnabled,
        ),
      ),
    enabled: !!scope() && enabled(),
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  }));
}

// ! The write hooks and imperative write factories that used to live here moved
// ! into `WidgetPersistenceService` (WAP-1540), which owns HTTP, list-cache patch,
// ! data invalidation, toasts and the `…Success` broadcasts in one place.

/**
 * Imperative counterpart of `injectWidgetDataQuery`, for Presentation Mode's
 * prefetch and its auto-refresh.
 *
 * Presentation Mode cannot be a plain per-card query: every slide's cards are
 * mounted at once, so enabling them would fan out all pages x all widgets at once
 * instead of the deliberate slide-by-slide progression. Instead the prefetcher
 * writes **the key the cards observe** and the cards stay disabled, rendering
 * straight from the cache entry.
 */
export function injectFetchWidgetData() {
  const widgetApi = inject(WidgetApiService);
  const queryClient = inject(QueryClient);

  return (scope: WidgetDataScope) =>
    queryClient.fetchQuery({
      queryKey: widgetDataQueryKey(scope),
      queryFn: () =>
        firstValueFrom(
          widgetApi.getWidgetData(
            scope.widgetId,
            scope.contentFilters,
            scope.languageId,
            scope.cacheEnabled,
          ),
        ),
      retry: false,
    });
}
